import { useIntl } from 'react-intl';

import config, { isEnabled } from 'Config';
import {
  ROUTE_ABOUT,
  ROUTE_DOWNLOAD,
  ROUTE_FEATURE_GOLD,
  ROUTE_FEATURE_PLATINUM,
  ROUTE_FEATURE_PLUS,
  ROUTE_FEATURE_PREMIUM,
  ROUTE_FEATURE_SELECT,
  ROUTE_FEATURE_SUBSCRIPTIONS,
  ROUTE_FEATURE_SWIPE,
  ROUTE_GIFT_CARD,
  ROUTE_GIFT_CARD_REDEEM,
} from 'Config/configs/routes/static';
import appendLangParam from 'Lib/utils/appendLangParam';
import appendLangPrefix from 'Lib/utils/appendLangPrefix';
import appendThemeParam from 'Lib/utils/appendThemeParam';

import { ThemeId } from 'Store/appSettings/types';

import type { MenuItem } from './types';

const { navMenuLinks: links } = config;

const menuData: MenuItem[] = [
  {
    id: 'NavMenu.products',
    href: ROUTE_FEATURE_SWIPE,
    items: [
      {
        id: 'NavMenu.premiumFeature',
        href: ROUTE_FEATURE_PREMIUM,
      },
      {
        id: 'NavMenu.subscriptionTiers',
        href: ROUTE_FEATURE_SUBSCRIPTIONS,
        items: [
          {
            id: 'tinderPlus',
            href: ROUTE_FEATURE_PLUS,
          },
          {
            id: 'tinderGold',
            href: ROUTE_FEATURE_GOLD,
          },
          {
            id: 'tinderPlatinum',
            href: ROUTE_FEATURE_PLATINUM,
          },
        ],
      },
      {
        id: 'TinderSelect.tinderSelect',
        href: ROUTE_FEATURE_SELECT,
        hasLocaleParam: true,
      },
    ],
  },
  {
    id: 'NavMenu.learn',
    href: ROUTE_ABOUT,
  },
  {
    id: 'NavMenu.community',
    href: links.safety,
    hasLocaleParam: true,
    items: [
      {
        href: links.communityGuidelines,
        id: 'NavMenu.communityGuidelines',
        hasLocaleParam: true,
      },
      {
        href: links.safety,
        id: 'NavMenu.safety',
        hasLocaleParam: true,
      },
      {
        href: links.safetyAndPolicy,
        id: 'NavMenu.safetyAndPolicy',
        hasLocaleParam: true,
      },
      {
        href: links.safetyAndReporting,
        id: 'NavMenu.safetyAndReporting',
        hasLocaleParam: true,
      },
      {
        href: links.security,
        id: 'NavMenu.security',
        hasLocaleParam: true,
      },
    ],
  },
  {
    id: 'NavMenu.support',
    href: config.navMenuLinks.faq.home,
    hasLocalePrefix: false,
  },
  {
    id: 'NavMenu.downloadApp',
    href: ROUTE_DOWNLOAD,
  },
];

if (isEnabled('giftCard')) {
  menuData.push({
    id: 'GiftCard.giftCards',
    href: ROUTE_GIFT_CARD,
    hasLocalePrefix: false,
    items: [
      {
        href: links.purchaseGiftCard,
        id: 'GiftCard.purchaseGiftCard',
        hasLocalePrefix: false,
      },
      {
        href: ROUTE_GIFT_CARD_REDEEM,
        id: 'GiftCard.redeemGiftCard',
        hasLocalePrefix: false,
      },
    ],
  });
}

const useMenuData = (theme?: ThemeId) => {
  const { locale } = useIntl();

  const localizeItems = (items: MenuItem[]) =>
    items.map<MenuItem>((item) => {
      const newItem = { ...item };

      if (item.hasLocaleParam) {
        newItem.href = appendLangParam(item.href, locale);
      } else if (item.hasLocalePrefix ?? true) {
        newItem.href = appendLangPrefix(item.href, locale);
      }

      if (theme) {
        newItem.href = appendThemeParam(newItem.href, theme);
      }

      if (item.items) {
        newItem.items = localizeItems(item.items);
      }

      return newItem;
    });

  return localizeItems(menuData);
};

export default useMenuData;
